import { Listener } from '@/types/listener.types';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  RetryContext
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { Notifier } from '@shared/notifier/types';
import { environment } from '@/environments/environment';

const STOP_RETRY = null;
const FIRST_CONNECT_RETRY_DELAY_MS = 5 * 1000;

/**
 * Provides a retry mechanism for when the SignalR connection is lost or cannot be established at all.
 * When the connection is lost due to no activity on the tab, it will only reconnect when the tab becomes active again.
 * Provides simplified configuration options for initializing signalR hubs.
 * It also provides lifecycle hooks for the signalR hub.
 *
 * Afspraken voor afgeleide listeners staan in CLAUDE.md (Real-time).
 */
export abstract class SignalRHubListener implements Listener {
  protected readonly hubConnection: HubConnection;
  private _isAvailable$ = new BehaviorSubject(false);
  private _isInitializing$ = new BehaviorSubject(false);
  private readonly _connectionLost$ = new BehaviorSubject(false);
  private _reconnectWhenDocumentVisible = false;
  private _closingOnPurpose = false;
  private _connecting?: Promise<void>;
  private _connectRetryCount = 0;
  private _connectRetryTimeout?: ReturnType<typeof setTimeout>;

  protected constructor(
    private readonly hubUrl: string,
    private readonly config: SignalRHubListenerConfig,
    protected readonly notifier: Notifier,
    protected readonly logger?: NGXLogger
  ) {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext: RetryContext) => this.nextRetryDelay(retryContext)
      })
      .build();

    this.registerLifecycleHandlers();
    this.listenForDocumentVisibilityChange();
  }

  private readonly _reconnect$ = new Subject<undefined>();

  get reconnect$(): Observable<undefined> {
    return this._reconnect$;
  }

  get isInitializing$(): Observable<boolean> {
    return this._isInitializing$;
  }

  get isAvailable$(): Observable<boolean> {
    return this._isAvailable$;
  }

  get isAvailable(): boolean {
    return this._isAvailable$.value;
  }

  /**
   * De verbinding is onbedoeld weg: herverbinden loopt, werd opgegeven (bv. tab verborgen) of
   * de eerste start mislukte. Niet bij bewust sluiten (`close`) en zolang er nooit gestart werd.
   */
  get connectionLost$(): Observable<boolean> {
    return this._connectionLost$;
  }

  get name(): string {
    return this.config.name;
  }

  async init(): Promise<void> {
    const disableHubs = JSON.parse(localStorage.getItem('disableHubs'));
    if (disableHubs) {
      return;
    }
    await this.connectOnce(false);
  }

  public async close() {
    this.cancelConnectRetry();
    this._reconnectWhenDocumentVisible = false;
    this._closingOnPurpose = true;
    this._isAvailable$.next(false);
    this._connectionLost$.next(false);
    await this.hubConnection.stop();
  }

  /**
   * Add super.beforeConnecting() before any other implementation when overriding method
   */
  async beforeConnecting(): Promise<void> {}

  /**
   *   Add super.afterConnected AFTER any other implementation in overriding method
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async afterConnected(isReconnect: boolean = false) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onInitializationFailed(e) {}

  /**
   * Registreert de handler als de enige voor dit hub-bericht. Opnieuw registreren, bv. in
   * afterConnected na een herverbinding, vervangt de handler in plaats van er één bij te zetten.
   */
  protected registerMessageHandler(methodName: string, handler: Parameters<HubConnection['on']>[1]): void {
    this.hubConnection.off(methodName);
    this.hubConnection.on(methodName, handler);
  }

  /** Retry-beleid van de automatische herverbinding (apart zodat het testbaar is). */
  protected nextRetryDelay(retryContext: RetryContext): number | null {
    if (retryContext.previousRetryCount > 1) {
      this.connectionToast('error', `Live verbinding verbroken met ${this.hubUrl}. Herconnecteren mislukt!`);
    }

    if (document.visibilityState === 'hidden') {
      this._reconnectWhenDocumentVisible = true;
      // Chrome automatically closes connection when the current tab becomes IDLE, causing an infinite loop with the retry mechanism
      return STOP_RETRY;
    }

    if (retryContext.elapsedMilliseconds >= this.config.incrementalRetryThresholdMS) {
      return this.config.incrementalRetryThresholdMS;
    }

    return retryContext.elapsedMilliseconds;
  }

  /** Voegt zich bij een lopende verbindingspoging, zodat gelijktijdige aanroepers maar één keer verbinden. */
  private connectOnce(isReconnect: boolean): Promise<void> {
    this._connecting ??= this.connect(isReconnect).finally(() => {
      this._connecting = undefined;
    });
    return this._connecting;
  }

  private async connect(isReconnect: boolean): Promise<void> {
    const statesIndicatingAlreadyInitialized: HubConnectionState[] = [
      HubConnectionState.Connecting,
      HubConnectionState.Reconnecting,
      HubConnectionState.Connected
    ];
    if (statesIndicatingAlreadyInitialized.includes(this.hubConnection.state)) {
      return;
    }

    try {
      this._closingOnPurpose = false;
      this._isInitializing$.next(true);
      await this.beforeConnecting();
      await this.startConnection();
      this._connectRetryCount = 0;
      this._isAvailable$.next(true);
      this._connectionLost$.next(false);
      this._isInitializing$.next(false);
      await this.afterConnected(isReconnect);
      this.updateIsAvailable();
    } catch (e) {
      this._isInitializing$.next(false);
      if (this.hubConnection.state !== HubConnectionState.Connected) {
        this._connectionLost$.next(true);
      }
      this.onInitializationFailed(e);
      this.retryConnect(isReconnect);
    }
  }

  /**
   * De automatische herverbinding van SignalR geldt enkel voor een verbinding die al eens open
   * was. Een mislukte start (bv. de api herstart net terwijl de app laadt) wordt hier opnieuw
   * geprobeerd: na 5, 10, 20, 40 en dan elke 60 s, of zodra een verborgen tab weer zichtbaar wordt.
   */
  private retryConnect(isReconnect: boolean): void {
    if (this.hubConnection.state !== HubConnectionState.Disconnected) {
      return;
    }

    if (document.visibilityState === 'hidden') {
      this._reconnectWhenDocumentVisible = true;
      return;
    }

    const delay = Math.min(
      FIRST_CONNECT_RETRY_DELAY_MS * 2 ** this._connectRetryCount,
      this.config.incrementalRetryThresholdMS
    );
    this._connectRetryCount++;
    this._connectRetryTimeout = setTimeout(() => {
      this._connectRetryTimeout = undefined;
      void this.connectOnce(isReconnect);
    }, delay);
  }

  private cancelConnectRetry(): void {
    clearTimeout(this._connectRetryTimeout);
    this._connectRetryTimeout = undefined;
    this._connectRetryCount = 0;
  }

  /**
   * Toasts over de live verbinding staan standaard UIT (runtime-config `liveConnectionToasts`
   * in env/env.js): bij elke release en elke korte storing gaf elke hub een rode en daarna een
   * groene toast, terwijl de verbinding zich vanzelf herstelt. Een langdurige onderbreking
   * toont de navbar subtiel (LiveConnectionStatusService). Weer aanzetten kan zonder rebuild.
   */
  private connectionToast(kind: 'error' | 'success', message: string): void {
    if (!environment.liveConnectionToasts) {
      return;
    }

    if (kind === 'error') {
      this.notifier.error(message);
    } else {
      this.notifier.success(message);
    }
  }

  /**
   * BELANGRIJK: één keer per listener, in de constructor. SignalR bewaart deze callbacks zolang
   * de HubConnection bestaat. Vroeger registreerde de onreconnected-callback zichzelf opnieuw,
   * waardoor het aantal handlers bij elke herverbinding verdubbelde: na n herverbindingen
   * 2^(n-1) toasts en afterConnected-aanroepen (en evenveel InitializeClientAfterConnected-
   * calls naar de server). Zie signal-r-hub-listener.spec.ts.
   *
   * isAvailable volgt de echte verbindingsstatus: meteen onwaar bij reconnecting en close, en na
   * elke hook afgeleid uit hubConnection.state (een trage hook mag een latere status niet overschrijven).
   */
  private registerLifecycleHandlers(): void {
    this.hubConnection.onreconnecting(async () => {
      this._isAvailable$.next(false);
      this._connectionLost$.next(true);
      await this.beforeConnecting();
    });

    this.hubConnection.onreconnected(async () => {
      this._reconnect$.next(undefined);
      this.logger?.log(`Hub on ${this.hubUrl} reconnected!`);
      this.connectionToast('success', `Live verbinding hersteld met ${this.hubUrl}`);
      this._connectionLost$.next(false);
      await this.afterConnected(true);
      this.updateIsAvailable();
    });

    // Herverbinden opgegeven (tab verborgen) of de server sloot: weg, tenzij we zelf sloten.
    this.hubConnection.onclose(() => {
      this._isAvailable$.next(false);
      this._connectionLost$.next(!this._closingOnPurpose);
    });
  }

  private listenForDocumentVisibilityChange() {
    document.addEventListener('visibilitychange', async () => {
      if (
        document.visibilityState === 'visible' &&
        this.hubConnection.state === HubConnectionState.Disconnected &&
        this._reconnectWhenDocumentVisible
      ) {
        this._reconnectWhenDocumentVisible = false;
        await this.connectOnce(true);
      }
    });
  }

  private async startConnection(): Promise<void> {
    try {
      await this.hubConnection.start();
    } catch {
      // for some reason the first one fails and the second one just succeeds, seems like sourcery to me
      await this.hubConnection.start();
    }
  }

  /** Volgt de echte verbindingsstatus, die tijdens een afgewachte hook veranderd kan zijn. */
  private updateIsAvailable(): void {
    const isAvailable = this.hubConnection.state === HubConnectionState.Connected;
    if (isAvailable !== this._isAvailable$.value) {
      this._isAvailable$.next(isAvailable);
    }
  }
}

export interface SignalRHubListenerConfig {
  readonly name: string;
  readonly incrementalRetryThresholdMS: number;
}
